import React, { createContext, useEffect } from 'react';

import useFetchPrograms from 'hooks/useFetchPrograms';
import { useRouter } from 'next/router';
import { useTranslation } from 'react-i18next';
import { ProgramType } from 'screens/Programs/list/ProgramList.types';
import { getProgramsByType } from 'screens/Programs/list/ProgramList.utils';

import { AISectionIds, AISectionProps } from '../utils/dashboardSideBar';

import { useNotification } from '~/context/NotificationContext';
import { useUserAccountHasBudget } from '~/features/dashboard/hooks/useUserAccountHasBudget';
import { AuthRole, useUser } from '~/lib/auth';

type IDashboardContext = {
  hasStandaloneAIPrograms: boolean;
  hasHumanLedCoachingPrograms: boolean;
  isOpsUser: boolean;
  isCustomerUser: boolean;
  hasBudget?: boolean;
  isLoading: boolean;
};

const DashboardContext = createContext<IDashboardContext | undefined>(undefined);

type DashboardProviderProps = {
  children: React.ReactElement;
};

function DashboardProvider({ children }: DashboardProviderProps) {
  const router = useRouter();
  const { roles } = useUser();

  const { t } = useTranslation('Dashboard');

  const { setNotification } = useNotification();
  const { hasBudget, isLoading: isLoadingBudget } = useUserAccountHasBudget();

  const { programs = [], loading: isLoadingPrograms } = useFetchPrograms();
  const allProgramsByType = getProgramsByType(programs);
  const hasStandaloneAIProgramsKey = Object.keys(allProgramsByType).includes(ProgramType.StandaloneAI);
  const hasStandaloneAIPrograms = hasStandaloneAIProgramsKey && allProgramsByType[ProgramType.StandaloneAI].length > 0;

  const hasHumanLedCoachingProgramsKey =
    Object.keys(allProgramsByType).includes(ProgramType.IndividualHolisticFramework) ||
    Object.keys(allProgramsByType).includes(ProgramType.IndividualCoachHubFramework) ||
    Object.keys(allProgramsByType).includes(ProgramType.Collective);

  const hasHumanLedCoachingPrograms =
    hasHumanLedCoachingProgramsKey &&
    (allProgramsByType[ProgramType.IndividualHolisticFramework].length > 0 ||
      allProgramsByType[ProgramType.IndividualCoachHubFramework].length > 0 ||
      allProgramsByType[ProgramType.Collective].length > 0);

  const isOpsUser = roles.has(AuthRole.OPERATIONS);
  const isCustomerUser = roles.has(AuthRole.CUSTOMER);

  // Combined loading state - dashboard is loading if either programs or budget data is still loading
  const isLoading = isLoadingPrograms || isLoadingBudget;

  useEffect(() => {
    if (!hasHumanLedCoachingPrograms && router.pathname === '/' && hasStandaloneAIPrograms) {
      router.push(AISectionProps[AISectionIds.AI_ENGAGEMENT]['href']);
    }
  }, [hasHumanLedCoachingPrograms, router, hasStandaloneAIPrograms]);

  useEffect(() => {
    // show toast once after terms are accepted
    const acceptedTerms = sessionStorage.getItem('acceptedTerms');
    if (acceptedTerms === 'true') {
      // show toast
      setNotification({
        type: 'success',
        message: t('ownershipAndAccess.notifications.termsAcceptedSuccess'),
      });
    }

    sessionStorage.removeItem('acceptedTerms');

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setNotification]);

  return (
    <DashboardContext.Provider
      value={{ hasStandaloneAIPrograms, hasHumanLedCoachingPrograms, hasBudget, isOpsUser, isCustomerUser, isLoading }}
    >
      {children}
    </DashboardContext.Provider>
  );
}

function useDashboard() {
  const context = React.useContext(DashboardContext);
  if (context === undefined) {
    throw new Error('useDashboard must be used within a DashboardProvider');
  }
  return context;
}

export { DashboardProvider, useDashboard };
